function getLocalStorage<TReturnType = string | Object | null>(key: string): TReturnType {
	if (typeof window === 'undefined') {
		return null as TReturnType;
	}

	let item = localStorage.getItem(key);

	if (item) {
		try {
			// test if item is a stringified object
			item = JSON.parse(item);
		} catch {
			// is a string
			return item as TReturnType;
		}

		// return the parsed object
		return item as TReturnType;
	}

	return null as TReturnType;
}

function setLocalStorage(key: string, value: string | Object): void {
	try {
		let toSaveValue = value;

		if (typeof toSaveValue === 'object') {
			toSaveValue = JSON.stringify(value);
		}

		localStorage.setItem(key, toSaveValue as string);
	} catch (err) {
		console.error('Error setting localStorage: ', err);
	}
}

function updateLocalStorage(key: string, obj: Object): void {
	const storedItem = getLocalStorage(key);
	if (storedItem && typeof storedItem === 'object') {
		try {
			localStorage.setItem(key, JSON.stringify({ ...storedItem, ...obj }));
		} catch (err) {
			console.error('Error updating localStorage: ', err);
		}
	}
}

function upsertLocalStorage(key: string, value: Object): void {
	const storedItem = getLocalStorage(key);
	if (!storedItem) {
		setLocalStorage(key, value);
	} else if (storedItem && typeof storedItem === 'object') {
		try {
			localStorage.setItem(key, JSON.stringify({ ...storedItem, ...value }));
		} catch (err) {
			console.error('Error upserting localStorage: ', err);
		}
	}
}

function removeLocalStorage(key: string): void {
	try {
		localStorage.removeItem(key);
	} catch (err) {
		console.error('Error removing localStorage: ', err);
	}
}

function clearLocalStorage(): void {
	try {
		localStorage.clear();
	} catch (err) {
		console.error('Error clearing localStorage: ', err);
	}
}

function removeLocalStorageWithKeyStartingBy(key: string) {
	try {
		Object.keys(window.localStorage).forEach((item) => {
			if (item.startsWith(key)) {
				localStorage.removeItem(item);
			}
		});
	} catch {}
}

export {
	clearLocalStorage,
	getLocalStorage,
	removeLocalStorage,
	removeLocalStorageWithKeyStartingBy,
	setLocalStorage,
	updateLocalStorage,
	upsertLocalStorage
};
